import React, { useContext, useEffect, useState } from "react";
import { ISetItemsPricesDto } from "../../../../dtos/documents/SetItemsPrices/ISetItemsPricesDto";
import { NewDocumentModalViewContext } from "../../../Shared/NewDocumentModalView";
import { useAppSelector } from "../../../../redux/hooks/hooks";
import { selectSubmitCounter } from "../../../../redux/slices/submitCounterSlice";
import useRunError from "../../../Shared/useRunError";
import SetItemsPricesDetailsSkeleton from "./SetItemsPricesDetailsSkeleton";
import { useTranslation } from "react-i18next";
import { AppEntities } from "../../../../config/entities";
import { importFromExcel, saveObjectToExcel } from "../../../../helpers/SaveObjectToExcel";
import { NFTabs } from "../../../Shared/NewForms/NFTabs";
import { NFTab } from "../../../Shared/NewForms/NFTab";
import { DocumentModalViewHeader } from "../../../Shared/DocumentModalViewHeader";
import { NFTabContainer } from "../../../Shared/NewForms/NFTabContainer";
import { SetItemsPricesDetailsRecords } from "./SetItemsPricesDetailsRecords";
import { SetItemsPricesAdditional } from "./SetItemsPricesAdditional";
import { SetItemsPricesDetailsMain } from "./SetItemsPricesDetailsMain";
import { FormProvider, useForm } from "react-hook-form";
import { SetItemsPricesModel } from "../../../../models/Documents/SetItemsPrices/SetItemsPricesModel";
import { DocumentSaveActionType } from "../../../../helpers/DocumentHelper";
import { SettlementPricesDetailFooter } from "./SettlementPricesDetailFooter";
import DocumentDetailViewToolbar from "../../../Shared/DetailViews/DocumentDetailViewToolbar";
import DetailViewPostButton from "../../../Shared/DetailViews/DetailViewPostButton";
import DetailViewSaveButton from "../../../Shared/DetailViews/DetailViewSaveButton";
import DetailViewCancelButton from "../../../Shared/DetailViewCancelButton";
import DetailViewToolbarMoreActions from "../../../Shared/DetailViews/DetailViewToolbarMoreActions";

interface IProps {
  model: ISetItemsPricesDto | null;
  modelUpdatedEvent: (newModel: ISetItemsPricesDto) => void;
}

const SetItemsPricesDetails = ({ model, modelUpdatedEvent }: IProps) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, dirtyFields, isDirty, isValid },
    clearErrors,
    reset,
    formState,
    getValues,
    watch,
    control,
    trigger,
    ...methods
  } = useForm<SetItemsPricesModel>();

  const modalViewContext = useContext(NewDocumentModalViewContext);
  const { postHandler, saveHandler, closeHandler, cancelHandler } = modalViewContext;

  const [tabIndex, setTabIndex] = useState(0);
  const [fileName, setFileName] = useState("Set Items Prices");

  const counter = useAppSelector(selectSubmitCounter);
  const runError = useRunError(isValid, errors, counter);

  useEffect(() => {
    if (errors.companyId?.type === "required" || errors.priceTypeId?.type === "required" || errors.currencyId?.type === "required") {
      runError();
    }
  }, [errors, runError]);

  useEffect(() => {
    register("products");
    if (model) {
      setValue("id", model.id);
      setValue("createdDate", model.createdDate);
      setValue("documentNumber", model.documentNumber);
      setValue("documentDate", model.documentDate);
      setValue("companyId", model.companyId);
      setValue("totalAmount", model.totalAmount);
      setValue("totalAmountVat", model.totalAmountVat);
      setValue("period", model.period);
      setValue("priceTypeId", model.priceTypeId);
      setValue("currencyId", model.currencyId);
      setValue("comment", model.comment);
      setValue("products", model.products);
    }
  }, [model]);

  const handleFormSubmit = async (action: DocumentSaveActionType, data: SetItemsPricesModel) => {
    if (action === "save") {
      const newModel: ISetItemsPricesDto = { ...model, ...data };
      await saveHandler(newModel, action);
    } else if (action === "post") {
      const newModel: ISetItemsPricesDto = { ...model, ...data };
      await saveHandler(newModel, action);
      await postHandler(action);
    }
  };

  if (!model) {
    return (
      <>
        <SetItemsPricesDetailsSkeleton />
      </>
    );
  }

  const exportToExcel = () => {
    const data = {
      ...model
    };
    const documentInfo = {
      title: AppEntities.SetItemsPrices.title,
      number: model.documentNumber,
      date: model.documentDate
    };
    saveObjectToExcel(data, documentInfo);
  };

  const handleUploadFile = (file: File) => {
    importFromExcel(file, (data: any) => {
      reset(data);
    });
  };

  return (
    <FormProvider
      getValues={getValues}
      setValue={setValue}
      register={register}
      control={control}
      trigger={trigger}
      reset={reset}
      watch={watch}
      handleSubmit={handleSubmit}
      formState={formState}
      clearErrors={clearErrors}
      {...methods}
    >
      <NFTabs
        onTabChange={(newIndex) => {
          setTabIndex(newIndex);
        }}
      >
        <NFTab title={"Main"} />
        <NFTab title={"Products"} />
        <NFTab title={"Additional"} />
      </NFTabs>

      <div className={"modal-container-height"}>
        {/* <DocumentModalViewHeader
          onPost={handleSubmit((data) => handleFormSubmit("post", data))}
          onSave={handleSubmit((data) => handleFormSubmit("save", data))}
          onClose={closeHandler}
          onCancel={cancelHandler}
          handleUploadFile={handleUploadFile}
          exportToExcel={exportToExcel}
          fileName={fileName}
          setFileName={setFileName}
        /> */}
        <DocumentDetailViewToolbar>
          <DetailViewPostButton onPost={handleSubmit((data) => handleFormSubmit("post", data))} />
          <DetailViewSaveButton onSave={handleSubmit((data) => handleFormSubmit("save", data))} />
          <DetailViewCancelButton onCancel={cancelHandler} />
          <DetailViewToolbarMoreActions handleUploadFile={handleUploadFile} exportToExcel={exportToExcel} fileName={fileName} setFileName={setFileName} />
        </DocumentDetailViewToolbar>

        <NFTabContainer index={0} currentIndex={tabIndex}>
          <SetItemsPricesDetailsMain />
        </NFTabContainer>
        <NFTabContainer index={1} currentIndex={tabIndex}>
          <SetItemsPricesDetailsRecords />
        </NFTabContainer>
        <NFTabContainer index={2} currentIndex={tabIndex}>
          <SetItemsPricesAdditional />
        </NFTabContainer>
      </div>
      <SettlementPricesDetailFooter />
    </FormProvider>
  );
};

export default SetItemsPricesDetails;
